import { useSyncExternalStore } from 'react';
import { getCallCenter } from '../CustomerDemo/callCenterStore';
import { DEMO_VERSION, demoState } from './khoDemo';
import {
  docById, hasControlled, nextCode, validateDoc, type DocLine, type Item, type KhoState, type NormLine, type StockDoc,
} from './khoLogic';

/**
 * Dữ liệu kho lưu ở localStorage — chạy front-end trước, sau này thay từng hàm khoApi bằng API.
 * Lần đầu mở sẽ tự nạp dữ liệu demo thẩm mỹ viện.
 */

const KEY = 'khothi.kho.v1';

const demoCustomers = () => {
  try {
    return getCallCenter().customers.filter(c => c.phones[0]).slice(0, 10).map(c => ({ name: c.name, phone: c.phones[0] }));
  } catch { return []; }
};
const freshDemo = (by?: string) => demoState(Date.now(), by, demoCustomers());

function load(): KhoState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw);
      const stale = (s?.demoVersion || 1) < DEMO_VERSION && Array.isArray(s?.docs) && s.docs.every((d: { id: string }) => d.id.startsWith('demo-'));
      if (s?.v === 1 && Array.isArray(s.docs) && !stale) return s;
    }
  } catch { /* dữ liệu hỏng → nạp lại demo */ }
  const s = freshDemo();
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* bỏ qua */ }
  return s;
}

let state: KhoState = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function set(fn: (s: KhoState) => KhoState): boolean {
  const next = fn(state);
  if (next === state) return true;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { return false; }
  state = next;
  emit();
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === KEY && e.newValue) { state = load(); emit(); }
  });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useKho = () => useSyncExternalStore(subscribe, () => state);
export const getKho = () => state;

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
export const newLineId = uid;

export type DocDraft = Omit<StockDoc, 'id' | 'code' | 'status' | 'createdBy' | 'createdAt' | 'log'> & { id?: string };
export interface Result { ok: boolean; errors?: string[]; id?: string }

const patchDoc = (id: string, fn: (d: StockDoc) => StockDoc) =>
  set(s => ({ ...s, docs: s.docs.map(d => (d.id === id ? fn(d) : d)) }));
const withLog = (d: StockDoc, by: string, text: string, at = Date.now()): StockDoc => ({ ...d, log: [...d.log, { at, by, text }] });

export const khoApi = {
  /** Tạo mới / sửa phiếu nháp (phiếu bị từ chối sửa lại sẽ về nháp) */
  saveDoc(draft: DocDraft, by: string): Result {
    const now = Date.now();
    const lines: DocLine[] = draft.lines.map(l => ({ ...l, id: l.id || uid() }));
    if (draft.id) {
      const cur = docById(state, draft.id);
      if (!cur) return { ok: false, errors: ['Không tìm thấy phiếu'] };
      if (cur.status !== 'draft' && cur.status !== 'rejected') return { ok: false, errors: ['Chỉ sửa được phiếu nháp hoặc bị từ chối'] };
      const ok = patchDoc(cur.id, d => withLog({ ...d, ...draft, lines, id: d.id, status: 'draft', rejectReason: undefined }, by, cur.status === 'rejected' ? 'Sửa lại phiếu bị từ chối' : 'Cập nhật phiếu'));
      return ok ? { ok, id: cur.id } : { ok, errors: ['Bộ nhớ trình duyệt đầy, không lưu được'] };
    }
    const id = uid();
    const doc: StockDoc = { ...draft, lines, id, code: nextCode(state, draft.type, draft.date), status: 'draft', createdBy: by, createdAt: now, log: [{ at: now, by, text: 'Tạo phiếu' }] };
    const ok = set(s => ({ ...s, docs: [...s.docs, doc] }));
    return ok ? { ok, id } : { ok, errors: ['Bộ nhớ trình duyệt đầy, không lưu được'] };
  },

  submitDoc(id: string, by: string): Result {
    const d = docById(state, id);
    if (!d || d.status !== 'draft') return { ok: false, errors: ['Phiếu không ở trạng thái nháp'] };
    const errors = validateDoc(state, d, Date.now());
    if (errors.length) return { ok: false, errors };
    patchDoc(id, x => withLog({ ...x, status: 'pending' }, by, 'Gửi duyệt'));
    return { ok: true, id };
  },

  /** Duyệt = ghi sổ kho. Thuốc kiểm soát bắt buộc có người duyệt chuyên môn. */
  approveDoc(id: string, by: string, expertBy?: string): Result {
    const d = docById(state, id);
    if (!d || d.status !== 'pending') return { ok: false, errors: ['Phiếu không ở trạng thái chờ duyệt'] };
    const errors = validateDoc(state, d, Date.now());
    if (hasControlled(state, d) && !expertBy) errors.push('Phiếu có thuốc kiểm soát — chọn người duyệt chuyên môn');
    if (errors.length) return { ok: false, errors };
    const at = Date.now();
    patchDoc(id, x => withLog({ ...x, status: 'approved', approvedBy: by, approvedAt: at, ...(expertBy ? { expertBy } : {}) }, by,
      `Duyệt phiếu${expertBy ? ` · chuyên môn: ${expertBy}` : ''}${x.type === 'transfer' ? ' — hàng đã rời kho xuất' : ''}`, at));
    return { ok: true, id };
  },

  rejectDoc(id: string, by: string, reason: string): Result {
    const d = docById(state, id);
    if (!d || d.status !== 'pending') return { ok: false, errors: ['Phiếu không ở trạng thái chờ duyệt'] };
    if (!reason.trim()) return { ok: false, errors: ['Nhập lý do từ chối'] };
    patchDoc(id, x => withLog({ ...x, status: 'rejected', rejectReason: reason.trim() }, by, `Từ chối: ${reason.trim()}`));
    return { ok: true, id };
  },

  cancelDoc(id: string, by: string): Result {
    const d = docById(state, id);
    if (!d || !['draft', 'pending', 'rejected'].includes(d.status)) return { ok: false, errors: ['Phiếu đã duyệt không hủy được — dùng phiếu kiểm kho để điều chỉnh'] };
    patchDoc(id, x => withLog({ ...x, status: 'cancelled' }, by, 'Hủy phiếu'));
    return { ok: true, id };
  },

  /** Kho nhận xác nhận đã nhận đủ hàng chuyển đến → hàng vào kho nhận */
  receiveTransfer(id: string, by: string, note?: string): Result {
    const d = docById(state, id);
    if (!d || d.type !== 'transfer' || d.status !== 'approved' || d.receivedAt) return { ok: false, errors: ['Phiếu không ở trạng thái đang chuyển'] };
    const at = Date.now();
    patchDoc(id, x => withLog({ ...x, receivedAt: at, receivedBy: by }, by, `Xác nhận đã nhận hàng${note ? `: ${note}` : ''}`, at));
    return { ok: true, id };
  },

  saveItem(item: Omit<Item, 'id'> & { id?: string }): Result {
    const code = item.code.trim().toUpperCase();
    if (!code || !item.name.trim()) return { ok: false, errors: ['Nhập mã và tên mặt hàng'] };
    if (state.items.some(i => i.code.toUpperCase() === code && i.id !== item.id)) return { ok: false, errors: [`Mã ${code} đã tồn tại`] };
    const clean: Item = { ...item, code, name: item.name.trim(), id: item.id || `it-${uid()}` };
    set(s => ({ ...s, items: item.id ? s.items.map(i => (i.id === item.id ? clean : i)) : [...s.items, clean] }));
    return { ok: true, id: clean.id };
  },

  setItemActive(id: string, active: boolean) {
    set(s => ({ ...s, items: s.items.map(i => (i.id === id ? { ...i, active } : i)) }));
  },

  saveNorm(serviceId: string, lines: NormLine[]) {
    const clean = lines.filter(l => l.itemId && l.qty > 0);
    set(s => ({ ...s, norms: { ...s.norms, [serviceId]: clean } }));
  },

  loadDemo(by?: string) { set(() => freshDemo(by)); },
};
